import { useEffect, useRef } from 'react'

import { ApolloQueryResult } from '@apollo/client'

import { IncidentsQuery } from '@/graphql/generated/operations'
import {
  Exact,
  IncidentFilterInput,
  IncidentOrderInput,
} from '@/graphql/generated/schemas'

import { NotificationDataIProps } from './types'

export type RefetchFunctionT = (
  variables?: Partial<
    Exact<{
      filter?: IncidentFilterInput
      first?: number
      last?: number
      orderBy?: IncidentOrderInput | IncidentOrderInput[]
    }>
  >
) => Promise<ApolloQueryResult<IncidentsQuery>>

export const INCIDENTS_BROADCAST_CHANNEL = 'INCIDENTS_BROADCAST_CHANNEL'
const THROTTLE_DELAY_MS = 5_000 // 5 seconds
export const useIncidentsBroadCastChannel = () => {
  const incidentsBroadcastChannelRef = useRef<BroadcastChannel | null>(null)

  useEffect(() => {
    incidentsBroadcastChannelRef.current = new BroadcastChannel(
      INCIDENTS_BROADCAST_CHANNEL
    )

    return () => {
      incidentsBroadcastChannelRef.current?.close()
      incidentsBroadcastChannelRef.current = null
    }
  }, [])

  const postIncidentsMessage = (data: NotificationDataIProps) => {
    const newIncidentMessage = {
      incidentDisplayId: data?.notification.incidentDisplayId,
      incidentId: data?.notification.incidentId,
    }
    // Broadcast out each time we have a unique Incident come in
    incidentsBroadcastChannelRef.current?.postMessage(newIncidentMessage)
  }

  return {
    incidentsBroadcastChannelRef,
    postIncidentsMessage,
  }
}

// Accepts any zero-arg refresh callback — it never forwards variables or reads
// the result, so a plain `() => void` (e.g. a combined multi-query refetch) is
// as valid as an Apollo `RefetchFunctionT`.
export const useIncidentsBroadCastChannelReceiver = (
  refetch: RefetchFunctionT | (() => void)
) => {
  const lastRefetchTime = useRef<number>(0)
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const bc = new BroadcastChannel(INCIDENTS_BROADCAST_CHANNEL)

    const handleIncidentMessage = () => {
      const now = Date.now()
      const timeSinceLastRefetch = now - lastRefetchTime.current
      // If 5+ seconds have passed
      const hasThrottleWindowExpired = timeSinceLastRefetch >= THROTTLE_DELAY_MS

      // Early return: execute refetch if throttle window has passed
      if (hasThrottleWindowExpired) {
        lastRefetchTime.current = now
        refetch()
        return
      }

      // Within throttle window: clear existing timer and schedule new one
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current)
      }

      const remainingTime = THROTTLE_DELAY_MS - timeSinceLastRefetch

      // Schedule refetch to execute after the remaining throttle time
      pendingTimerRef.current = setTimeout(() => {
        lastRefetchTime.current = Date.now()
        pendingTimerRef.current = null
        refetch()
      }, remainingTime)
    }

    // Will reload the Incidents each time a new notification comes in
    // Throttled to prevent excessive refetches (max 1 per 5 seconds)
    bc.onmessage = handleIncidentMessage

    // Upon end of lifecycle, will close connection and clear pending timer
    return () => {
      bc.close()
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current)
      }
    }
  }, [refetch])
}
